<template>
  <section class="is-full-height is-full-width">
    <div class="is-flex is-justify-content-space-between">
      <div class="has-text-primary has-text-weight-semibold m-b-30">
        <span class="is-clickable is-size-3">{{ `<<` }}</span>
        <span
          class="is-clickable is-size-4 has-text-weight-semibold"
          @click="back"
          :data-e2e="LightDiagnosticCypressAttribute.buttons.BACK_TO_SELECTION"
        >
          {{ $t('standardplan.change-selection') }}
        </span>
      </div>
      <FormButtonToggle
        v-if="billingPlanForm && showCoupleToggle"
        class="is-pulled-right"
        :values="coupleTypes"
        :read-only="isPartyTypeReadOnly"
        value-by-prop="value"
        v-model="partyType"
        :data-e2e="LightDiagnosticCypressAttribute.buttons.COUPLE_TYPE_TOGGLE"
        @select="onCoupleTypeSelect"
      />
    </div>
    <div class="is-full-height is-full-width">
      <SelfServeBillingCardSkeleton v-if="isLoading" />
      <div
        v-else-if="billingPlanForm?.isCustomPlan"
        class="p-5 is-full-width has-background-secondary has-border-radius has-text-centered"
        :data-e2e="LightDiagnosticCypressAttribute.views.CUSTOM_BILLING_PLAN_CARD"
      >
        <p class="is-size-2 has-text-weight-semibold m-b-15">
          {{ $t('lightdiagnostic.custom-plan-required') }}
        </p>
        <p class="m-b-20">{{ $t('lightdiagnostic.we-offer-more-customized-solutions') }}</p>
        <ButtonView type="is-primary">
          {{ $t('lightdiagnostic.book-free-consultation') }}
        </ButtonView>
      </div>
      <VaultPlanBillingCard
        v-else-if="!!billingPlanForm"
        :billing-plan="billingPlanForm"
        :has-call-to-action="hasCallToAction"
        class="m-t-10"
        @on-click="onClick"
        :data-e2e="LightDiagnosticCypressAttribute.views.BILLING_PLAN_CARD"
      />
    </div>
  </section>
</template>

<script>
import { LightDiagnosticForm } from '@/modules/lightdiagnostic/models/LightDiagnosticForm';
import SelfServeBillingCardSkeleton from '@/modules/estate/components/skeletons/SelfServeBillingCardSkeleton.vue';
import LightDiagnosticSituation from '@/modules/lightdiagnostic/components/presentational/LightDiagnosticSituation.vue';
import VaultPlanBillingCard from '@/modules/estate/components/presentational/VaultPlanBillingCard.vue';
import LightDiagnosticService from '@/modules/lightdiagnostic/services/LightDiagnosticService';
import LightDiagnosticExternalService from '@/modules/lightdiagnostic/services/LightDiagnosticExternalService';
import { VaultBillingPlanFormFactory } from '@/modules/estate/models/VaultBillingPlanFormFactory';
import { LightDiagnosticCypressAttribute } from '@/modules/lightdiagnostic/constants/LightDiagnosticCypressAttribute';
import FormButtonToggle from '@/modules/core/form/components/FormButtonToggle.vue';
import { EstatePlanPartyType } from '@/modules/estate/enums/EstatePlanPartyType';
import { FormFlowMixin } from '@/modules/core/form/mixins/FormFlowMixin';
import { HasCurrentEstateMixin } from '@/modules/core/app/mixins/HasCurrentEstateMixin';
import { OpensFreeConsultModalMixin } from '@/modules/meeting/components/mixins/OpensFreeConsultModalMixin';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'LightDiagnosticBillingPlan',
  emits: ['on-couple-type-select', 'on-goals-input', 'on-situations-input', 'custom-flow-change', 'on-plan-selected', 'on-plan-calculated', 'flow-change'],
  mixins: [FormFlowMixin, HasCurrentEstateMixin, HasAuthUserMixin, OpensFreeConsultModalMixin],
  components: { ButtonView, FormButtonToggle, VaultPlanBillingCard, SelfServeBillingCardSkeleton },
  props: {
    value: {
      type: LightDiagnosticForm,
      required: true
    },
    jurisdictionId: {
      type: Number,
      required: true
    },
    showBillingPlan: {
      type: Boolean
    },
    showCoupleToggle: {
      type: Boolean,
      default: true
    },
    hasCallToAction: {
      type: Boolean,
      default: false
    },
    isExternal: {
      type: Boolean,
      default: false
    }
  },
  static: {
    LightDiagnosticCypressAttribute,
    coupleTypes: EstatePlanPartyType.getValues()
  },
  data() {
    return {
      isLoading: false,
      billingPlanForm: null,
      partyType: EstatePlanPartyType.INDIVIDUAL.key
    };
  },
  computed: {
    isPartyTypeReadOnly() {
      return this.billingPlanForm?.isStandardPlan || this.billingPlanForm?.isCustomPlan;
    }
  },
  methods: {
    async onCoupleTypeSelect(value) {
      const companyId = this.currentEstate?.company?.id;
      try {
        this.isLoading = true;
        if (value === EstatePlanPartyType.INDIVIDUAL.key) {
          this.billingPlanForm = this.billingPlanForm.setAsIndividual();
          await this.billingPlanForm.loadPlanPrices(
            this.jurisdictionId,
            companyId,
            this.value.partnershipInvitationType
          );
        }
        if (value === EstatePlanPartyType.COUPLE.key) {
          this.billingPlanForm = this.billingPlanForm.setAsCouples();
          await this.billingPlanForm.loadPlanPrices(
            this.jurisdictionId,
            companyId,
            this.value.partnershipInvitationType
          );
        }
      } finally {
        this.isLoading = false;
        this.$emit('on-couple-type-select', value);
      }
    },
    continueWithPlan() {
      this.$emit('on-goals-input', []);
      this.$emit('on-situations-input', []);
      this.$emit('custom-flow-change');
    },
    onClick(billingPlan) {
      this.$emit('on-plan-selected', billingPlan, this.value);

      if (billingPlan.isAlreadyOnPlan) {
        this.continueWithPlan();
      }
    },
    async loadBillingPlan() {
      try {
        this.isLoading = true;
        let calculatedPlanForm = null;
        if (this.isExternal) {
          calculatedPlanForm = await LightDiagnosticExternalService.calculateBillingPlan({
            ...this.value,
            jurisdictionId: this.jurisdictionId
          });
        } else {
          calculatedPlanForm = await LightDiagnosticService.calculateBillingPlan({
            ...this.value,
            jurisdictionId: this.jurisdictionId,
            companyId: this.authUser?.professional?.companyId
          });
        }
        this.buildPlan(calculatedPlanForm);
        this.$emit('on-plan-calculated', this.billingPlanForm, this.value);
      } finally {
        this.isLoading = false;
      }
    },
    buildPlan(calculatedPlanForm) {
      const isAlreadyOnPlan =
        this.currentEstate?.billing?.billingPlanMapping?.planId ===
        calculatedPlanForm.billingPlanId;
      this.billingPlanForm = VaultBillingPlanFormFactory.build(
        calculatedPlanForm.billingPlanId,
        calculatedPlanForm.price,
        calculatedPlanForm.yearlyPrice,
        !this.currentEstate?.isInQuebecJurisdiction,
        calculatedPlanForm.isSaasBilling
      )
        ?.setIsRecommended(true)
        .setIsAlreadyOnPlan(isAlreadyOnPlan)
        .setChargeRecurringFee(calculatedPlanForm.chargeRecurringFee);
    },
    back() {
      this.$emit('on-goals-input', []);
      this.$emit('on-situations-input', []);
      this.$emit('flow-change', LightDiagnosticSituation);
    }
  },
  mounted() {
    this.loadBillingPlan();
  }
};
</script>
